import { isEmpty, noop } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEnvelope,
  faEnvelopeDot,
  faEnvelopeOpen,
  faPen,
  faShelves,
} from '@fortawesome/pro-regular-svg-icons'
import {
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  Truncate,
  VerticalDivider,
  useAuth,
} from '@labrav/react-components'
import { OfferRecipients } from '../../../types/flyout'
import { useStyles } from './ViewRecipients.styles'
import { useTimeZone } from '../../utils/hooks/useTimeZone'
import { productDetailDate } from '../../utils/productDetailDate'
import { DetailRow } from '../DetailRow/DetailRow'
import { RecipentStakeholderRow } from '../RecipientStakeholderRow/RecipentStakeholderRow'
import { SectionWrapper } from '../SectionWrapper/SectionWrapper'
import { Dispatch, SetStateAction, useMemo } from 'react'
import clsx from 'clsx'
import { EditRecipients } from '../EditRecipients/EditRecipients'
import {
  ViewValueWithTruncate,
  getSafeString,
  useCurrentCloudProvider,
} from '@labrav/common'
import { providerToAuthCloud, CloudAccountProvider } from '@labrav/common'
import { useTheme } from '@mui/material/styles'

export const ViewRecipients = ({
  offerRecipients,
  isRecipientEditActionDisabled,
  addRecipientsResaleToolTipMessage,
  hideEditActionRecipient,
  handleSubmitRecipient,
  openEditRecipient,
  setOpenEditRecipient,
  isUpdateLoading,
}: {
  offerRecipients: OfferRecipients[]
  isRecipientEditActionDisabled?: boolean
  addRecipientsResaleToolTipMessage?: string
  hideEditActionRecipient?: boolean
  handleSubmitRecipient?: (offerRecipients: OfferRecipients[]) => void
  setOpenEditRecipient: Dispatch<SetStateAction<boolean>>
  openEditRecipient: boolean
  isUpdateLoading?: boolean
}) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { timeZone } = useTimeZone()
  const { loading: authLoading, canDo } = useAuth()
  const cloud = useCurrentCloudProvider()
  const canWriteMarketplace = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloud.toLowerCase() as CloudAccountProvider),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloud])

  const permissionMessageString =
    'This action is disabled because you have read-only access'

  const editAction = (
    <div
      className={clsx(
        classes.editActionLabel,
        isRecipientEditActionDisabled || !canWriteMarketplace
          ? classes.disabledEdit
          : ''
      )}
      data-testid={'edit-action'}
    >
      <FontAwesomeIcon
        icon={faPen}
        style={{
          width: '14px',
          height: '14px',
          fontSize: '14px',
          padding: '0px 9px 0px 0px',
          color: theme.palette.primary.main,
          opacity:
            isRecipientEditActionDisabled || !canWriteMarketplace
              ? '39%'
              : '100%',
        }}
      />
      <div>Edit</div>
    </div>
  )

  return (
    <>
      <SectionWrapper
        heading="Recipients"
        icon={faEnvelope}
        className={classes.rightSection}
        handleAction={() => {
          if (!canWriteMarketplace || isRecipientEditActionDisabled) return
          setOpenEditRecipient(true)
        }}
        actionDisabled={isRecipientEditActionDisabled || !canWriteMarketplace}
        actionDisabledTooltip={
          !canWriteMarketplace
            ? permissionMessageString
            : isRecipientEditActionDisabled
              ? addRecipientsResaleToolTipMessage
              : ''
        }
        varient="small"
        {...(!hideEditActionRecipient ? { actionLabel: editAction } : {})}
        children={
          <div className={classes.section} data-testid={'offer-recipient-view'}>
            {(offerRecipients || []).map((item, index) => {
              const { fullDateTime: sentAtFullDateTime } = productDetailDate(
                item.sentAt || '',
                timeZone
              )
              const { fullDateTime: viewedAtFullDateTime } = productDetailDate(
                item.lastViewedAt || '',
                timeZone
              )

              const sentAt = `Sent at ${sentAtFullDateTime}`
              const viewedAt = `Viewed at ${viewedAtFullDateTime}`

              const dateValue = !isEmpty(item?.lastViewedAt)
                ? viewedAt
                : !isEmpty(item?.sentAt)
                  ? sentAt
                  : ''

              const isTitleExist = !!item?.title

              return (
                <DetailRow
                  key={index}
                  borderBottom={index !== offerRecipients.length - 1}
                  className={clsx(
                    classes.rowItem,
                    index === offerRecipients.length - 1 && classes.lastRowItem
                  )}
                >
                  <RecipentStakeholderRow
                    title={
                      <div className={classes.recipientTitle}>
                        <div className={classes.recipientTitleWrapper}>
                          <div
                            data-testid={'recipient-name'}
                            className={clsx(
                              classes.recipientName,
                              !isTitleExist && classes.recipientNameWithoutTitle
                            )}
                          >
                            <ViewValueWithTruncate
                              width="100%"
                              value={`${item?.firstName || ''} ${
                                item?.lastName || ''
                              }`.trim()}
                            />
                          </div>

                          {item?.title && (
                            <>
                              <VerticalDivider height="24px" />
                              <div
                                data-testid={'recipient-title'}
                                className={classes.recipientTitleName}
                              >
                                <ViewValueWithTruncate
                                  width="100%"
                                  value={item?.title}
                                />
                              </div>
                            </>
                          )}
                        </div>

                        {(!isEmpty(item?.lastViewedAt) ||
                          !isEmpty(item?.sentAt)) && (
                          <LabraToolTipV2
                            description={
                              !isEmpty(item?.lastViewedAt)
                                ? 'Viewed'
                                : 'Not viewed'
                            }
                            tooltipPlacmentGap="8px"
                            tooltipPlacement={'bottom'}
                          >
                            <FontAwesomeIcon
                              icon={
                                !isEmpty(item?.lastViewedAt)
                                  ? faEnvelopeOpen
                                  : faEnvelopeDot
                              }
                              style={{
                                width: '18px',
                                height: '20px',
                                fontSize: '16px',
                                color: theme.palette.labraText.description,
                              }}
                            />
                          </LabraToolTipV2>
                        )}
                      </div>
                    }
                    value={
                      <Truncate width={'90%'} clipboardTooltip showIcon={false}>
                        {getSafeString(item?.email)}
                      </Truncate>
                    }
                    description={dateValue}
                    keyName={'recipient'}
                  />
                </DetailRow>
              )
            })}
          </div>
        }
      />

      <ModalConfirmV2
        title={'Edit recipients'}
        content={
          <EditRecipients
            offerRecipients={offerRecipients}
            handleCloseRecipients={() => setOpenEditRecipient(false)}
            handleSubmit={handleSubmitRecipient}
            isUpdateLoading={isUpdateLoading}
            cloud={cloud}
          />
        }
        hideCancelButton={true}
        open={openEditRecipient}
        hideAcceptButton={true}
        onAccept={noop}
        onDecline={() => setOpenEditRecipient(false)}
        variant={ModalSizeVariants.large}
        icon={<FontAwesomeIcon style={{ height: '14px' }} icon={faShelves} />}
      />
    </>
  )
}
